/**
 * Notification Context for Toast-style notifications
 * Replaces browser alerts with React-based notifications
 */
import React, { createContext, useContext, useState, useCallback } from 'react';
import { Toast, ToastContainer } from 'react-bootstrap';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);

  const addNotification = useCallback((notification) => {
    const id = Date.now().toString(36) + Math.random().toString(36).substr(2);
    const newNotification = {
      id,
      timestamp: new Date(),
      autohide: true,
      delay: 5000,
      ...notification
    };

    setNotifications(prev => [...prev, newNotification]);

    // Auto-remove notification after delay if autohide is true
    if (newNotification.autohide) {
      setTimeout(() => {
        removeNotification(id);
      }, newNotification.delay);
    }

    return id;
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  // Convenience methods for different notification types
  const showSuccess = useCallback((message, options = {}) => {
    return addNotification({
      type: 'success',
      title: options.title || 'Success',
      message,
      variant: 'success',
      ...options
    });
  }, [addNotification]);

  const showError = useCallback((message, options = {}) => {
    // Enhanced error display with request ID support
    let details = '';
    if (options.requestId) {
      details += `Request ID: ${options.requestId}`;
      if (options.code) {
        details += ` | Code: ${options.code}`;
      }
    } else if (options.code) {
      details += `Error Code: ${options.code}`;
    }
    
    if (options.retryable) {
      details += details ? ' | ' : '';
      details += 'This error can be retried';
    }

    return addNotification({
      type: 'error',
      title: options.title || 'Error',
      message,
      variant: 'danger',
      autohide: false, // Errors should be manually dismissed
      details,
      requestId: options.requestId,
      ...options
    });
  }, [addNotification]);

  const showWarning = useCallback((message, options = {}) => {
    return addNotification({
      type: 'warning',
      title: options.title || 'Warning',
      message,
      variant: 'warning',
      ...options
    });
  }, [addNotification]);

  const showInfo = useCallback((message, options = {}) => {
    return addNotification({
      type: 'info',
      title: options.title || 'Information',
      message,
      variant: 'info',
      ...options
    });
  }, [addNotification]);

  const value = {
    notifications,
    addNotification,
    removeNotification,
    clearAllNotifications,
    showSuccess,
    showError,
    showWarning,
    showInfo
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationDisplay 
        notifications={notifications}
        onClose={removeNotification}
      />
    </NotificationContext.Provider>
  );
};

const NotificationDisplay = ({ notifications, onClose }) => {
  return (
    <ToastContainer 
      position="top-end" 
      className="p-3"
      style={{ zIndex: 9999 }}
    >
      {notifications.map((notification) => (
        <Toast
          key={notification.id}
          show={true}
          onClose={() => onClose(notification.id)}
          bg={notification.variant}
          autohide={notification.autohide}
          delay={notification.delay}
        >
          <Toast.Header closeButton={true}>
            <strong className="me-auto">{notification.title}</strong>
            <small className="text-muted">
              {notification.timestamp.toLocaleTimeString()}
            </small>
          </Toast.Header>
          <Toast.Body className={notification.variant === 'light' ? 'text-dark' : 'text-white'}>
            {notification.message}
            {notification.details && (
              <div className="mt-2 small">
                {notification.details}
              </div>
            )}
            {notification.requestId && (
              <div className="mt-2 d-flex justify-content-between align-items-center">
                <small className="text-muted">
                  Request ID: {notification.requestId}
                </small>
                <button
                  className="btn btn-sm btn-outline-light"
                  onClick={() => {
                    navigator.clipboard.writeText(notification.requestId);
                    // Could add a mini toast here for copy confirmation
                  }}
                  title="Copy Request ID"
                >
                  Copy ID
                </button>
              </div>
            )}
          </Toast.Body>
        </Toast>
      ))}
    </ToastContainer>
  );
};